/**
 * Снимок для сравнения: код редактора + текстовый ответ (поле в условии).
 */
export function buildAttemptSnapshot(codeSnapshot, studentTextAnswer, workspaceState) {
  const ws =
    workspaceState && typeof workspaceState === 'object'
      ? JSON.stringify(workspaceState)
      : '';
  return `${String(codeSnapshot ?? '').trim()}\n---\n${String(studentTextAnswer ?? '').trim()}\n---\n${ws}`;
}

/**
 * Загрузить последнюю попытку по задаче, пункту и/или списку.
 * listId — запасной ключ, если id пункта сменился.
 */
export async function fetchLatestAttempt(api, taskSlug, listItemId, listId = null) {
  const params = { task: taskSlug };
  if (listItemId) params.task_list_item = listItemId;
  if (listId) params.task_list = listId;
  const res = await api.get('/tasks/attempts/', { params });
  const arr = Array.isArray(res.data) ? res.data : res.data?.results ?? [];
  return arr[0] ?? null;
}

/**
 * Загрузить черновик (student, task[, task_list]).
 */
export async function fetchTaskDraft(api, taskSlug, listId = null) {
  const params = { task: taskSlug };
  if (listId) params.task_list = listId;
  try {
    const res = await api.get('/tasks/drafts/', { params });
    return res.data ?? null;
  } catch (e) {
    if (e?.response?.status === 404) return null;
    throw e;
  }
}

/**
 * Upsert черновика.
 */
export async function saveTaskDraft(api, { taskSlug, listId = null, listItemId = null, code, workspaceState }) {
  const body = {
    task: taskSlug,
    code: String(code ?? ''),
    workspace_state: workspaceState && typeof workspaceState === 'object' ? workspaceState : {},
  };
  if (listId) body.task_list = Number(listId);
  if (listItemId) body.task_list_item = listItemId;
  const res = await api.put('/tasks/drafts/', body);
  return res.data;
}

/**
 * POST новая попытка или PATCH последней, если снимок совпадает.
 * testsResult — объект для merge в tests_result (runner + answer_*).
 */
export async function recordTaskAttempt(api, payload) {
  const {
    taskSlug,
    taskId,
    listItemId,
    lessonSessionId,
    codeSnapshot,
    workspaceState,
    studentTextAnswer,
    selfReportStatus,
    selfComment,
    testsResult,
  } = payload;

  const latest = await fetchLatestAttempt(api, taskSlug, listItemId);
  const newSnap = buildAttemptSnapshot(codeSnapshot, studentTextAnswer, workspaceState);
  const oldSnap = latest
    ? buildAttemptSnapshot(
      latest.code_snapshot,
      latest.tests_result?.student_text_answer,
      latest.workspace_state,
    )
    : null;

  const mergedForPatch =
    latest && typeof latest.tests_result === 'object' && latest.tests_result !== null
      ? { ...latest.tests_result, ...(testsResult || {}) }
      : { ...(testsResult || {}) };
  if (studentTextAnswer !== undefined && studentTextAnswer !== null) {
    mergedForPatch.student_text_answer = String(studentTextAnswer);
  }

  const wsPayload =
    workspaceState && typeof workspaceState === 'object' ? workspaceState : {};

  if (latest && newSnap === oldSnap) {
    await api.patch(`/tasks/attempts/${latest.id}/`, {
      self_report_status: selfReportStatus ?? latest.self_report_status,
      self_comment: selfComment !== undefined ? selfComment : latest.self_comment,
      code_snapshot: String(codeSnapshot ?? ''),
      workspace_state: wsPayload,
      tests_result: mergedForPatch,
    });
    return { mode: 'patch', id: latest.id };
  }

  const mergedForPost = { ...(testsResult || {}) };
  if (studentTextAnswer !== undefined && studentTextAnswer !== null) {
    mergedForPost.student_text_answer = String(studentTextAnswer);
  }

  await api.post('/tasks/attempts/', {
    task: taskId,
    lesson_session: lessonSessionId ? Number(lessonSessionId) : null,
    task_list_item: listItemId,
    code_snapshot: String(codeSnapshot ?? ''),
    workspace_state: wsPayload,
    tests_result: mergedForPost,
    self_report_status: selfReportStatus ?? 'NOT_TRIED',
    self_comment: selfComment || '',
  });
  return { mode: 'post' };
}

/** MVP: нормализация для сравнения с expected_answer */
export function normalizeAnswerText(s) {
  return String(s ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

export function isChoiceAnswerType(answerType) {
  return answerType === 'single_choice' || answerType === 'multiple_choice';
}

export function isOrderAnswerType(answerType) {
  return answerType === 'order';
}

/** Общий тег пробных тестов (slug `test`, имя «Тест»). */
export const TEST_TAG_SLUG = 'test';

export function hasTestTag(tags) {
  if (!Array.isArray(tags)) return false;
  return tags.some((t) => {
    if (t == null) return false;
    if (typeof t === 'string') return t === TEST_TAG_SLUG || t === 'Тест';
    return t.slug === TEST_TAG_SLUG || t.name === 'Тест';
  });
}

export const ORDER_MAX_INDENT = 4;

/** Отступ по горизонтальному сдвигу: один слот = 4 пробела. */
export function indentFromHorizontalDelta(startIndent, deltaX, slotWidth, maxIndent = ORDER_MAX_INDENT) {
  const start = Math.max(0, Math.min(maxIndent, Math.floor(Number(startIndent)) || 0));
  const slot = Number(slotWidth);
  if (!Number.isFinite(slot) || slot <= 0) return start;
  const dx = Number(deltaX);
  if (!Number.isFinite(dx)) return start;
  return Math.max(0, Math.min(maxIndent, start + Math.round(dx / slot)));
}

/**
 * Нужны ли редактор кода, ввод и вывод.
 * Прячем у пробных тестов (тег «Тест») и у типов со своим UI (выбор, «собери программу»).
 * Каталог вроде ЕГЭ-17 — число в бланк, но тега «Тест» нет: редактор нужен.
 * Пустой тип — как код (урок ещё без активной задачи).
 * Предпочтительно использовать resolveWorkspace(task).
 */
export function needsCodeWorkspace(answerType, tags) {
  return resolveWorkspace({
    answer_type: answerType,
    tags,
    workspace_kind: 'code',
  }).showCodeEditor;
}

export const WORKSPACE_KIND = {
  CODE: 'code',
  NONE: 'none',
  GRAPH_TABLE: 'graph_table',
  TREE_TABLE: 'tree_table',
  SPREADSHEET: 'spreadsheet',
  CODE_TURTLE: 'code_turtle',
  CODE_ROBOT: 'code_robot',
  CODE_CANVAS: 'code_canvas',
};

export const WORKSPACE_KIND_LABELS = {
  code: 'Редактор кода',
  none: 'Без черновика',
  graph_table: 'Граф и таблица',
  tree_table: 'Дерево и таблица',
  spreadsheet: 'Таблица (формулы)',
  code_turtle: 'Код и холст черепахи',
  code_robot: 'Код и поле робота',
  code_canvas: 'Код и интерактивный холст',
};

/** Номера ЕГЭ из тегов task-N / ege-N / «Задание N» / «ЕГЭ-N». */
export function examNumbersFromTags(tags) {
  const nums = new Set();
  if (!Array.isArray(tags)) return nums;
  const slugRe = /^(?:task|ege)-(\d+)$/i;
  const nameRe = /^(?:ЕГЭ|Задание)\s*[-\u2010-\u2015]?\s*(\d+)\s*$/i;
  for (const t of tags) {
    if (t == null) continue;
    const slug = typeof t === 'string' ? t : String(t.slug || '');
    const name = typeof t === 'string' ? '' : String(t.name || '');
    let m = slugRe.exec(slug.trim());
    if (m) nums.add(Number(m[1]));
    m = nameRe.exec(name.trim());
    if (m) nums.add(Number(m[1]));
  }
  return nums;
}

/** Дефолт workspace по номеру ЕГЭ (если на задаче ещё kind=code). */
export function inferWorkspaceKindFromExamTags(tags, answerType) {
  const nums = examNumbersFromTags(tags);
  if (nums.has(1)) return WORKSPACE_KIND.GRAPH_TABLE;
  if (nums.has(4)) return WORKSPACE_KIND.TREE_TABLE;
  if (nums.has(3) || nums.has(18)) return WORKSPACE_KIND.SPREADSHEET;
  if (nums.has(6) && answerType === 'code') return WORKSPACE_KIND.CODE_TURTLE;
  return null;
}

/**
 * Какой workspace показывать для задачи.
 * answer_type / тег «Тест» перекрывают workspace_kind (квизы без черновика).
 */
export function resolveWorkspace(task) {
  const answerType = task?.answer_type ?? '';
  const tags = task?.tags;
  const config =
    task?.workspace_config && typeof task.workspace_config === 'object' && !Array.isArray(task.workspace_config)
      ? task.workspace_config
      : {};

  if (isChoiceAnswerType(answerType) || isOrderAnswerType(answerType) || hasTestTag(tags)) {
    return {
      kind: WORKSPACE_KIND.NONE,
      showWorkspace: false,
      showCodeEditor: false,
      showRunner: false,
      showTurtle: false,
      showCanvas: false,
      showCollab: false,
      title: '',
      config,
    };
  }

  const raw = String(task?.workspace_kind || WORKSPACE_KIND.CODE).trim() || WORKSPACE_KIND.CODE;
  let kind = Object.values(WORKSPACE_KIND).includes(raw) ? raw : WORKSPACE_KIND.CODE;

  // Пока на задаче default «code» — подставить инструмент по тегу номера ЕГЭ
  // (1/3/4/18 — объяснялки без кода; 6 code — код+холст). Явный kind не трогаем.
  if (kind === WORKSPACE_KIND.CODE) {
    const inferred = inferWorkspaceKindFromExamTags(tags, answerType);
    if (inferred) kind = inferred;
  }

  if (kind === WORKSPACE_KIND.NONE) {
    return {
      kind,
      showWorkspace: false,
      showCodeEditor: false,
      showRunner: false,
      showTurtle: false,
      showCanvas: false,
      showCollab: false,
      title: '',
      config,
    };
  }

  const showCodeEditor =
    kind === WORKSPACE_KIND.CODE
    || kind === WORKSPACE_KIND.CODE_TURTLE
    || kind === WORKSPACE_KIND.CODE_ROBOT
    || kind === WORKSPACE_KIND.CODE_CANVAS;
  const showTool =
    kind === WORKSPACE_KIND.GRAPH_TABLE
    || kind === WORKSPACE_KIND.TREE_TABLE
    || kind === WORKSPACE_KIND.SPREADSHEET;
  const showTurtle = kind === WORKSPACE_KIND.CODE_TURTLE;
  const showCanvas =
    kind === WORKSPACE_KIND.CODE_TURTLE
    || kind === WORKSPACE_KIND.CODE_ROBOT
    || kind === WORKSPACE_KIND.CODE_CANVAS;

  return {
    kind,
    showWorkspace: showCodeEditor || showTool,
    showCodeEditor,
    showRunner: showCodeEditor,
    showTurtle,
    showCanvas,
    showCollab: showCodeEditor || showTool,
    title: WORKSPACE_KIND_LABELS[kind] || 'Черновик',
    config,
  };
}

/** Id вариантов из строки expected_answer / ответа ученика: "a,c" → ["a","c"] */
export function parseChoiceIds(s) {
  return String(s ?? '')
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);
}

/** Стабильная строка id для multiple_choice (сортировка, без дублей). */
export function formatChoiceIds(ids, { sort = true } = {}) {
  const cleaned = [...new Set((ids || []).map((id) => String(id).trim()).filter(Boolean))];
  if (sort) cleaned.sort((a, b) => a.localeCompare(b, 'en'));
  return cleaned.join(',');
}

export function getChoiceOptions(answerSpec) {
  const opts = answerSpec && typeof answerSpec === 'object' ? answerSpec.options : null;
  if (!Array.isArray(opts)) return [];
  return opts
    .filter((o) => o && o.id != null && String(o.id).trim() !== '')
    .map((o) => ({
      id: String(o.id).trim(),
      text: o.text == null ? '' : String(o.text),
    }));
}

/** Буквы a, b, … по позиции на экране (после перемешивания). */
export function withChoiceDisplayLabels(options) {
  const used = [];
  return (options || []).map((opt) => {
    const label = nextChoiceOptionId(used);
    used.push(label);
    return { ...opt, label };
  });
}

export function shuffleArray(items, rng = Math.random) {
  const arr = [...(items || [])];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = arr[i];
    arr[i] = arr[j];
    arr[j] = tmp;
  }
  return arr;
}

/**
 * Перемешать варианты и повесить отображаемые буквы.
 * Порядок кэшируется в sessionStorage по seed (список + задача),
 * чтобы при возврате к вопросу и после обновления страницы он не прыгал.
 * Проверка ответа по-прежнему идёт по исходному id.
 */
export function resolveShuffledChoiceOptions(options, { seed = '', shuffle = true } = {}) {
  const raw = options || [];
  if (!shuffle || raw.length <= 1) {
    return withChoiceDisplayLabels(raw);
  }
  const key = seed ? `choiceShuffle:${seed}` : '';
  let savedIds = null;
  if (key && typeof sessionStorage !== 'undefined') {
    try {
      const saved = sessionStorage.getItem(key);
      if (saved) savedIds = JSON.parse(saved);
    } catch {
      savedIds = null;
    }
  }
  const byId = new Map(raw.map((o) => [o.id, o]));
  let ordered = Array.isArray(savedIds)
    ? savedIds.map((id) => byId.get(String(id))).filter(Boolean)
    : [];
  if (ordered.length !== raw.length) {
    ordered = shuffleArray(raw);
    if (key && typeof sessionStorage !== 'undefined') {
      try {
        sessionStorage.setItem(key, JSON.stringify(ordered.map((o) => o.id)));
      } catch {
        /* quota / private mode */
      }
    }
  }
  return withChoiceDisplayLabels(ordered);
}

export function formatChoiceRevealText(expectedAnswer, options) {
  const ids = parseChoiceIds(expectedAnswer);
  if (!ids.length) return '';
  const byId = new Map(
    (options || []).map((o) => [normalizeAnswerText(o.id), o]),
  );
  return ids
    .map((id) => {
      const opt = byId.get(normalizeAnswerText(id));
      if (!opt) return id;
      const letter = opt.label || opt.id;
      const text = String(opt.text ?? '').trim();
      return text ? `${letter}. ${text}` : String(letter);
    })
    .join('\n');
}

export function getOrderItems(answerSpec) {
  const raw = answerSpec && typeof answerSpec === 'object' ? answerSpec.items : null;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((o) => o && o.id != null && String(o.id).trim() !== '')
    .map((o) => {
      const indentRaw = o.indent;
      const indent = Number.isFinite(Number(indentRaw)) ? Math.max(0, Math.floor(Number(indentRaw))) : null;
      return {
        id: String(o.id).trim(),
        text: o.text == null ? '' : String(o.text),
        indent,
      };
    });
}

/** Несколько верных сборок через `|`: «a,b,c|a,c,b». Первая — для «показать ответ». */
export function parseOrderExpectedAlternatives(s) {
  return String(s ?? '')
    .split('|')
    .map((alt) => parseChoiceIds(alt))
    .filter((ids) => ids.length > 0);
}

function rowsFromExpectedIds(ids, answerSpec) {
  const byId = new Map(getOrderItems(answerSpec).map((o) => [normalizeAnswerText(o.id), o]));
  return ids.map((id) => {
    const item = byId.get(normalizeAnswerText(id));
    const indent = Number.isFinite(Number(item?.indent)) ? Math.max(0, Math.floor(Number(item.indent))) : 0;
    return { id: normalizeAnswerText(id), indent };
  });
}

function orderRowsEqual(a, b) {
  if (a.length !== b.length) return false;
  return a.every((row, i) => row.id === b[i].id && row.indent === b[i].indent);
}

/** «a:0,b:1» или «a,b» → [{id, indent}] */
export function parseOrderAnswer(s) {
  return String(s ?? '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const colon = part.lastIndexOf(':');
      if (colon <= 0) {
        return { id: part, indent: 0 };
      }
      const id = part.slice(0, colon).trim();
      const indent = Number.parseInt(part.slice(colon + 1), 10);
      return {
        id,
        indent: Number.isFinite(indent) ? Math.max(0, indent) : 0,
      };
    })
    .filter((row) => row.id);
}

export function formatOrderAnswer(rows) {
  return (rows || [])
    .map((row) => `${String(row.id).trim()}:${Number.isFinite(Number(row.indent)) ? Math.max(0, Math.floor(Number(row.indent))) : 0}`)
    .filter((part) => part !== ':0' && !part.startsWith(':'))
    .join(',');
}

export function resolveShuffledOrderItems(items, { seed = '', shuffle = true } = {}) {
  const raw = items || [];
  if (!shuffle || raw.length <= 1) return raw;
  const key = seed ? `orderShuffle:${seed}` : '';
  let savedIds = null;
  if (key && typeof sessionStorage !== 'undefined') {
    try {
      const saved = sessionStorage.getItem(key);
      if (saved) savedIds = JSON.parse(saved);
    } catch {
      savedIds = null;
    }
  }
  const byId = new Map(raw.map((o) => [o.id, o]));
  let ordered = Array.isArray(savedIds)
    ? savedIds.map((id) => byId.get(String(id))).filter(Boolean)
    : [];
  if (ordered.length !== raw.length) {
    ordered = shuffleArray(raw);
    if (key && typeof sessionStorage !== 'undefined') {
      try {
        sessionStorage.setItem(key, JSON.stringify(ordered.map((o) => o.id)));
      } catch {
        /* quota / private mode */
      }
    }
  }
  return ordered;
}

export function formatOrderRevealProgram(expectedAnswer, items) {
  const alts = parseOrderExpectedAlternatives(expectedAnswer);
  const ids = alts[0] || [];
  if (!ids.length) return '';
  const byId = new Map((items || []).map((o) => [normalizeAnswerText(o.id), o]));
  return ids
    .map((id) => {
      const item = byId.get(normalizeAnswerText(id));
      if (!item) return '';
      const level = Number.isFinite(Number(item.indent)) ? Math.max(0, Math.floor(Number(item.indent))) : 0;
      return `${'    '.repeat(level)}${item.text}`;
    })
    .filter((line) => line !== '')
    .join('\n');
}

function checkOrderAnswer(studentAnswer, expectedAnswer, answerSpec) {
  if (expectedAnswer == null || String(expectedAnswer).trim() === '') return null;
  const alts = parseOrderExpectedAlternatives(expectedAnswer);
  if (!alts.length) return null;
  const student = parseOrderAnswer(studentAnswer).map((row) => ({
    id: normalizeAnswerText(row.id),
    indent: row.indent,
  }));
  return alts.some((ids) => orderRowsEqual(student, rowsFromExpectedIds(ids, answerSpec)));
}

function checkChoiceAnswer(studentAnswer, expectedAnswer, answerType) {
  if (expectedAnswer == null || String(expectedAnswer).trim() === '') return null;
  if (answerType === 'multiple_choice') {
    const a = new Set(parseChoiceIds(studentAnswer).map(normalizeAnswerText));
    const b = new Set(parseChoiceIds(expectedAnswer).map(normalizeAnswerText));
    if (b.size === 0) return null;
    if (a.size !== b.size) return false;
    for (const id of a) {
      if (!b.has(id)) return false;
    }
    return true;
  }
  return normalizeAnswerText(studentAnswer) === normalizeAnswerText(expectedAnswer);
}

/**
 * Проверка ответа. Для choice — сравнение id (у multiple — как множества).
 * @returns {boolean|null} null если проверять нечего
 */
export function checkAnswerAgainstExpected(studentAnswer, expectedAnswer, answerType, answerSpec) {
  if (isChoiceAnswerType(answerType)) {
    return checkChoiceAnswer(studentAnswer, expectedAnswer, answerType);
  }
  if (isOrderAnswerType(answerType)) {
    return checkOrderAnswer(studentAnswer, expectedAnswer, answerSpec);
  }
  if (expectedAnswer == null || String(expectedAnswer).trim() === '') return null;
  return normalizeAnswerText(studentAnswer) === normalizeAnswerText(expectedAnswer);
}

/** Следующий id варианта: a, b, … z, затем aa, ab, … */
export function nextChoiceOptionId(existingIds) {
  const used = new Set((existingIds || []).map((id) => normalizeAnswerText(id)));
  const alphabet = 'abcdefghijklmnopqrstuvwxyz';
  for (const ch of alphabet) {
    if (!used.has(ch)) return ch;
  }
  for (let i = 0; i < alphabet.length; i += 1) {
    for (let j = 0; j < alphabet.length; j += 1) {
      const id = alphabet[i] + alphabet[j];
      if (!used.has(id)) return id;
    }
  }
  let n = 1;
  while (used.has(String(n))) n += 1;
  return String(n);
}
